import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, addDoc, query, where, orderBy, serverTimestamp, doc, getDoc } from "firebase/firestore";
import app from "./firebase";
import { useCookies } from "react-cookie";
import { Helmet } from "react-helmet";
import {
    Leaf, Search, Calendar, User, MessageSquare, ArrowRight, ChevronLeft,
    Send, Sparkles, TrendingUp, Award, Clock
} from "lucide-react";

const db = getFirestore(app);

export default function BlogPage() {
    const [cookies] = useCookies(["username", "name"]);
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const q = query(collection(db, "blogs"), orderBy("timestamp", "desc"));
                const snap = await getDocs(q);
                setBlogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching blogs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    useEffect(() => {
        if (selectedBlog) {
            const fetchComments = async () => {
                try {
                    const q = query(
                        collection(db, "blog_comments"),
                        where("blogId", "==", selectedBlog.id),
                        orderBy("timestamp", "asc")
                    );
                    const snap = await getDocs(q);
                    setComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (error) {
                    console.error("Error fetching comments:", error);
                }
            };
            fetchComments();
            window.scrollTo(0, 0);
        }
    }, [selectedBlog]);

    const handlePostComment = async () => {
        if (!cookies.username) {
            alert("Please log in to comment.");
            return;
        }
        if (!newComment.trim()) return;

        setSubmittingComment(true);
        try {
            const commentData = {
                blogId: selectedBlog.id,
                userId: cookies.username,
                userName: cookies.name || "User",
                comment: newComment,
                timestamp: serverTimestamp()
            };
            const docRef = await addDoc(collection(db, "blog_comments"), commentData);
            setComments([...comments, { id: docRef.id, ...commentData, timestamp: new Date() }]);
            setNewComment("");
        } catch (error) {
            console.error("Error posting comment:", error);
        } finally {
            setSubmittingComment(false);
        }
    };

    const filteredBlogs = blogs.filter(b =>
        b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
                <div style={{ textAlign: "center" }}>
                    <Sparkles size={40} className="animate-pulse" color="var(--color-primary)" />
                    <p style={{ marginTop: "12px", color: "var(--color-gray-500)", fontWeight: 600 }}>Loading Agrivestia Blog...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Inter', sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
            <Helmet>
                <title>{selectedBlog ? `${selectedBlog.title} — Agrivestia Blog` : "Agrivestia Blog — Agricultural Insights"}</title>
            </Helmet>

            {/* Navigation Bar */}
            <nav style={{
                background: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                position: "sticky",
                top: 0,
                zIndex: 100,
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                padding: "16px 0"
            }}>
                <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div
                        onClick={() => setSelectedBlog(null)}
                        style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                    >
                        <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, #22c55e, #15803d)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(34,197,94,0.3)" }}>
                            <Leaf size={20} color="white" />
                        </div>
                        <span style={{ fontWeight: 800, fontSize: "20px", color: "#1e293b", letterSpacing: "-0.02em" }}>Agrivestia <span style={{ color: "#22c55e" }}>Blog</span></span>
                    </div>
                    <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                        <a href="/" style={{ textDecoration: "none", color: "#64748b", fontSize: "14px", fontWeight: 600 }}>Home</a>
                        <a href="/market_dashboard" style={{ textDecoration: "none", color: "#64748b", fontSize: "14px", fontWeight: 600 }}>Marketplace</a>
                        <button
                            style={{ background: "#1e293b", color: "white", border: "none", padding: "8px 18px", borderRadius: "8px", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
                            onClick={() => { if (!cookies.username) window.location.href = "/register"; }}
                        >
                            {cookies.username ? `Hi, ${cookies.name || "Farmer"}` : "Join Us"}
                        </button>
                    </div>
                </div>
            </nav>

            <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px 24px" }}>

                {!selectedBlog ? (
                    <>
                        {/* Header Section */}
                        <div style={{ textAlign: "center", marginBottom: "60px" }}>
                            <h1 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, color: "#0f172a", marginBottom: "16px", letterSpacing: "-0.03em" }}>
                                Agricultural <span style={{ color: "#22c55e" }}>Insights</span>
                            </h1>
                            <p style={{ color: "#64748b", fontSize: "18px", maxWidth: "600px", margin: "0 auto 32px" }}>
                                Discover the latest trends, tips, and news in the world of smart farming and agricultural investment.
                            </p>
                            <div style={{ maxWidth: "500px", margin: "0 auto", position: "relative" }}>
                                <Search style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} size={20} />
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    style={{
                                        width: "100%", padding: "16px 16px 16px 48px", borderRadius: "16px", border: "1px solid #e2e8f0",
                                        fontSize: "16px", outline: "none", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                                        transition: "all 0.2s"
                                    }}
                                    onFocus={e => { e.target.style.borderColor = "#22c55e"; e.target.style.boxShadow = "0 0 0 4px rgba(34,197,94,0.1)"; }}
                                    onBlur={e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "0 4px 6px -1px rgba(0,0,0,0.05)"; }}
                                />
                            </div>
                        </div>

                        {/* Featured Post (if any) */}
                        {filteredBlogs.length > 0 && searchQuery === "" && (
                            <div
                                onClick={() => setSelectedBlog(filteredBlogs[0])}
                                style={{
                                    background: "white", borderRadius: "24px", overflow: "hidden", display: "flex",
                                    cursor: "pointer", marginBottom: "60px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.05)",
                                    flexDirection: window.innerWidth < 768 ? "column" : "row"
                                }}
                            >
                                <div style={{ flex: 1.2, height: "400px", background: "#f1f5f9" }}>
                                    <img
                                        src={filteredBlogs[0].image || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80"}
                                        alt={filteredBlogs[0].title}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                </div>
                                <div style={{ flex: 1, padding: "40px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                    <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                                        <span style={{ background: "#f0fdf4", color: "#16a34a", padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 700 }}>FEATURED</span>
                                        <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 600 }}>10 MIN READ</span>
                                    </div>
                                    <h2 style={{ fontSize: "32px", fontWeight: 800, color: "#0f172a", marginBottom: "20px", lineHeight: 1.2 }}>{filteredBlogs[0].title}</h2>
                                    <p style={{ color: "#64748b", lineHeight: 1.7, marginBottom: "32px" }}>
                                        {filteredBlogs[0].content.substring(0, 200)}...
                                    </p>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "auto" }}>
                                        <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <User size={20} color="#64748b" />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: "14px" }}>{filteredBlogs[0].author}</div>
                                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>{filteredBlogs[0].timestamp?.toDate ? filteredBlogs[0].timestamp.toDate().toLocaleDateString() : "Recently"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Grid Section */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "32px" }}>
                            {(searchQuery === "" ? filteredBlogs.slice(1) : filteredBlogs).map(blog => (
                                <div
                                    key={blog.id}
                                    onClick={() => setSelectedBlog(blog)}
                                    style={{
                                        background: "white", borderRadius: "20px", overflow: "hidden",
                                        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)", cursor: "pointer",
                                        transition: "transform 0.3s ease", display: "flex", flexDirection: "column"
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-8px)"}
                                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                                >
                                    <div style={{ height: "200px", background: "#f1f5f9" }}>
                                        <img
                                            src={blog.image || "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=600&q=80"}
                                            alt={blog.title}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                    </div>
                                    <div style={{ padding: "24px", flex: 1, display: "flex", flexDirection: "column" }}>
                                        <div style={{ display: "flex", gap: "10px", marginBottom: "12px", fontSize: "11px", fontWeight: 700, color: "#22c55e" }}>
                                            <Calendar size={14} /> {blog.timestamp?.toDate ? blog.timestamp.toDate().toLocaleDateString() : "Recently"}
                                        </div>
                                        <h3 style={{ fontSize: "20px", fontWeight: 800, color: "#1e293b", marginBottom: "12px", lineHeight: 1.4 }}>{blog.title}</h3>
                                        <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6, marginBottom: "20px" }}>
                                            {blog.content.substring(0, 120)}...
                                        </p>
                                        <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <User size={12} color="#94a3b8" />
                                                </div>
                                                <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>{blog.author}</span>
                                            </div>
                                            <ArrowRight size={18} color="#22c55e" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredBlogs.length === 0 && (
                            <div style={{ textAlign: "center", padding: "80px 0" }}>
                                <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                                    <Search size={32} color="#cbd5e1" />
                                </div>
                                <h3>No articles found</h3>
                                <p style={{ color: "#64748b" }}>Try different keywords or browse our latest posts.</p>
                            </div>
                        )}
                    </>
                ) : (
                    /* Single Blog View */
                    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                        <button
                            onClick={() => setSelectedBlog(null)}
                            style={{ background: "none", border: "none", color: "#64748b", fontSize: "14px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", marginBottom: "32px" }}
                        >
                            <ChevronLeft size={18} /> Back to all posts
                        </button>

                        <img
                            src={selectedBlog.image || "https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=1200&q=80"}
                            alt={selectedBlog.title}
                            style={{ width: "100%", height: "450px", objectFit: "cover", borderRadius: "24px", marginBottom: "40px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}
                        />

                        <div style={{ marginBottom: "16px", display: "flex", gap: "20px", alignItems: "center" }}>
                            <span style={{ background: "#f0fdf4", color: "#22c55e", padding: "6px 16px", borderRadius: "20px", fontSize: "12px", fontWeight: 700 }}>INSIGHTS</span>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center", color: "#94a3b8", fontSize: "13px" }}>
                                <Clock size={16} /> 10 MIN READ
                            </div>
                        </div>

                        <h1 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, color: "#0f172a", marginBottom: "24px", lineHeight: 1.1 }}>
                            {selectedBlog.title}
                        </h1>

                        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "48px", paddingBottom: "24px", borderBottom: "1px solid #e2e8f0" }}>
                            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <User size={24} color="#64748b" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, color: "#1e293b" }}>{selectedBlog.author}</div>
                                <div style={{ fontSize: "13px", color: "#94a3b8" }}>{selectedBlog.timestamp?.toDate ? selectedBlog.timestamp.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : "Recently Published"}</div>
                            </div>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <button style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#f1f5f9", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                    <Award size={18} color="#64748b" />
                                </button>
                            </div>
                        </div>

                        <div style={{
                            fontSize: "18px",
                            lineHeight: 1.8,
                            color: "#334155",
                            marginBottom: "60px",
                            whiteSpace: "pre-wrap"
                        }}>
                            {selectedBlog.content}
                        </div>

                        {/* Newsletter or Sidebar alike box */}
                        <div style={{ background: "#1e293b", borderRadius: "24px", padding: "40px", color: "white", marginBottom: "32px", textAlign: "center" }}>
                            <h2 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px" }}>Enjoyed this article?</h2>
                            <p style={{ color: "#94a3b8", marginBottom: "32px" }}>Join 10,000+ farmers receiving our weekly updates on agricultural technology.</p>
                            <div style={{ display: "flex", gap: "12px", maxWidth: "400px", margin: "0 auto" }}>
                                <input
                                    type="email"
                                    placeholder="name@email.com"
                                    style={{ flex: 1, padding: "12px 16px", borderRadius: "10px", border: "none", outline: "none" }}
                                />
                                <button style={{ background: "#22c55e", color: "white", padding: "12px 24px", borderRadius: "10px", border: "none", fontWeight: 700 }}>Join</button>
                            </div>
                        </div>

                        {/* Guest Post Section */}
                        <div style={{
                            background: "#f0fdf4",
                            border: "1px dashed #22c55e",
                            borderRadius: "24px",
                            padding: "40px",
                            marginBottom: "60px",
                            textAlign: "center"
                        }}>
                            <div style={{ width: "64px", height: "64px", background: "white", borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 10px 15px -3px rgba(34,197,94,0.1)" }}>
                                <Send size={32} color="#22c55e" />
                            </div>
                            <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#16a34a", marginBottom: "12px" }}>Become a Contributor</h2>
                            <p style={{ color: "#475569", fontSize: "16px", lineHeight: 1.6, maxWidth: "600px", margin: "0 auto 24px" }}>
                                Want to share your story or agricultural expertise? We welcome guest posts from our community!
                                Send your content to <a href="mailto:support@agrivestia.com" style={{ color: "#22c55e", fontWeight: 700, textDecoration: "none" }}>support@agrivestia.com</a>.
                            </p>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "white", padding: "8px 16px", borderRadius: "12px", fontSize: "14px", color: "#64748b", fontWeight: 600 }}>
                                <Sparkles size={16} color="#eab308" /> All submissions are subject to editorial review
                            </div>
                        </div>

                        {/* Comments Section */}
                        <div style={{ background: "white", borderRadius: "24px", padding: "40px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)" }}>
                            <h3 style={{ fontSize: "22px", fontWeight: 800, marginBottom: "32px", display: "flex", alignItems: "center", gap: "12px" }}>
                                <MessageSquare size={24} color="#22c55e" /> Discussion ({comments.length})
                            </h3>

                            {!cookies.username ? (
                                <div style={{ padding: "20px", background: "#f8fafc", borderRadius: "16px", textAlign: "center", marginBottom: "40px", border: "1px dashed #e2e8f0" }}>
                                    <p style={{ color: "#64748b", fontWeight: 600 }}>Please <a href="/login" style={{ color: "#22c55e" }}>log in</a> to join the conversation.</p>
                                </div>
                            ) : (
                                <div style={{ marginBottom: "48px" }}>
                                    <textarea
                                        placeholder="What are your thoughts?"
                                        value={newComment}
                                        onChange={e => setNewComment(e.target.value)}
                                        style={{
                                            width: "100%", padding: "16px", borderRadius: "16px", border: "1px solid #e2e8f0",
                                            minHeight: "120px", outline: "none", background: "#f8fafc", fontSize: "15px",
                                            marginBottom: "16px", transition: "all 0.2s"
                                        }}
                                        onFocus={e => e.target.style.borderColor = "#22c55e"}
                                        onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                                    />
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <button
                                            onClick={handlePostComment}
                                            disabled={submittingComment || !newComment.trim()}
                                            style={{
                                                background: "#22c55e", color: "white", border: "none", padding: "12px 24px",
                                                borderRadius: "10px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px",
                                                cursor: "pointer", opacity: submittingComment ? 0.7 : 1
                                            }}
                                        >
                                            {submittingComment ? "Posting..." : <><Send size={18} /> Post Comment</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                                {comments.map(c => (
                                    <div key={c.id} style={{ display: "flex", gap: "16px" }}>
                                        <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <User size={20} color="#94a3b8" />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                                <span style={{ fontWeight: 800, fontSize: "14px", color: "#1e293b" }}>{c.userName}</span>
                                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{c.timestamp?.toDate ? c.timestamp.toDate().toLocaleDateString() : "Just now"}</span>
                                            </div>
                                            <p style={{ fontSize: "15px", color: "#475569", lineHeight: 1.6, margin: 0 }}>{c.comment}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Internal CSS for simple global styles */}
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        body {
          margin: 0;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        * {
          box-sizing: border-box;
        }

        /* Customize scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
        </div>
    );
}
